import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  InvoiceStatusBadge,
  InvoiceTypeBadge,
} from '@/modules/billing/components';
import { useInvoices } from '@/modules/billing/hooks';
import {
  INVOICE_STATUS_LABELS,
  INVOICE_TYPE_LABELS,
  type InvoiceStatus,
  type InvoiceType,
} from '@/modules/billing/types';
import { filterInvoices } from '@/modules/billing/utils/invoiceFilters';
import { useClients } from '@/modules/commercial/hooks';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

export function InvoicesListPage() {
  const { invoices, loading, error } = useInvoices();
  const { clients } = useClients();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('billing.write');

  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<InvoiceType | 'all'>('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () =>
      filterInvoices(invoices, {
        status: statusFilter,
        type: typeFilter,
        search,
      }),
    [invoices, statusFilter, typeFilter, search],
  );

  const clientName = (id: string | null) =>
    id ? (clients.find((c) => c.id === id)?.name ?? '—') : '—';

  return (
    <section>
      <div className="inventory-page-header">
        <h1>Facturas</h1>
        {canWrite ? (
          <Link
            to={`${modulePaths.billing}/facturas/nueva`}
            className="auth-button inventory-page-header__btn"
          >
            Nueva factura interna
          </Link>
        ) : null}
      </div>

      <div className="reservation-filters users-form-grid">
        <label className="auth-label">
          Estado
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as InvoiceStatus | 'all')
            }
          >
            <option value="all">Todos</option>
            {(Object.keys(INVOICE_STATUS_LABELS) as InvoiceStatus[]).map((s) => (
              <option key={s} value={s}>
                {INVOICE_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="auth-label">
          Tipo
          <select
            value={typeFilter}
            onChange={(e) =>
              setTypeFilter(e.target.value as InvoiceType | 'all')
            }
          >
            <option value="all">Todos</option>
            {(Object.keys(INVOICE_TYPE_LABELS) as InvoiceType[]).map((t) => (
              <option key={t} value={t}>
                {INVOICE_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="auth-label">
          Buscar
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Número o notas"
          />
        </label>
      </div>

      {loading ? <p className="auth-muted">Cargando…</p> : null}
      {error ? <p className="auth-error">{error}</p> : null}

      {!loading && filtered.length === 0 ? (
        <p className="auth-muted">No hay facturas con los filtros aplicados.</p>
      ) : null}

      {!loading && filtered.length > 0 ? (
        <table className="users-table">
          <thead>
            <tr>
              <th>Número</th>
              <th>Tipo</th>
              <th>Estado</th>
              <th>Cliente</th>
              <th>Emisión</th>
              <th>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((inv) => (
              <tr key={inv.id}>
                <td>{inv.invoiceNumber || '—'}</td>
                <td>
                  <InvoiceTypeBadge type={inv.type} />
                </td>
                <td>
                  <InvoiceStatusBadge status={inv.status} />
                </td>
                <td>{clientName(inv.clientId)}</td>
                <td>{inv.issueDate}</td>
                <td>
                  {inv.total > 0
                    ? `$ ${inv.total.toLocaleString('es-AR')}`
                    : '—'}
                </td>
                <td>
                  <Link
                    to={`${modulePaths.billing}/facturas/${inv.id}`}
                    className="link-button"
                  >
                    Ver
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}
